import { MAX_REDEEMABLE_POINTS_PER_ORDER } from 'constants/shop.js';

/**
 * Calculates perk-related data for a product card.
 *
 * @param {Object} params - The parameters object
 * @param {number} params.loyaltyPointsAvailable - Total loyalty points available, typically from user's account data
 * @param {number} params.loyaltyPointsBalance - Loyalty points remaining for the current order, less any points redeemed in the cart
 * @param {Object} params.productData - Product data object
 * @param {boolean} params.showMembershipPrice - Whether to show membership price
 * @param {string} params.variant - The product card variant
 * @example
 * const perksData = getPerksData({
 *   loyaltyPointsAvailable: 600,
 *   loyaltyPointsBalance: 500,
 *   productData: { plusMemberPointPrice: 100, pointPrice: 150 },
 *   showMembershipPrice: true,
 *   variant: 'PERK',
 * });
 *
 * @returns {Object} Object containing perk-related data
 * @returns {number} returns.pointPrice - A Plus Membership aware point price for the item
 * @returns {boolean} returns.isPerk - Whether the product is a perk
 * @returns {boolean} returns.isPerkRedeemable - Whether the perk is redeemable
 */
const getPerksData = ({ loyaltyPointsAvailable, loyaltyPointsBalance, productData, showMembershipPrice, variant }) => {
    if (!['PERK', 'PERK-REDEEMED'].includes(variant)) {
        return {
            pointPrice: 0, // Not used for non-perk variants
            isPerk: false,
            isPerkRedeemable: false,
        };
    }

    const pointPrice = showMembershipPrice ? productData.plusMemberPointPrice : productData.pointPrice;

    if (pointPrice <= 0) {
        return {
            pointPrice: 0,
            isPerk: false,
            isPerkRedeemable: false,
        };
    }

    const wouldExceedMaxRedeemablePoints =
        loyaltyPointsAvailable - loyaltyPointsBalance + pointPrice > MAX_REDEEMABLE_POINTS_PER_ORDER;
    const isPerkRedeemable = loyaltyPointsBalance >= pointPrice && !wouldExceedMaxRedeemablePoints;

    return {
        pointPrice,
        isPerk: true,
        isPerkRedeemable,
    };
};

/**
 * Calculates gift-with-purchase related data for a product card.
 * Only allows one gift to be redeemed per order.
 *
 * @param {Object} params - The parameters object
 * @param {Object} params.productData - Product data object
 * @param {Array} params.selections - Current cart selections
 * @param {string} params.variant - The product card variant
 * @returns {Object} Object containing gift-related data
 * @returns {boolean} returns.isGwp - Whether this is a gift with purchase item
 * @returns {boolean} returns.isGiftEditable - Whether the gift can be added/removed (no other gift in cart, or this gift is selected)
 * @returns {boolean} returns.isGiftRedeemed - Whether this specific gift is already in the cart
 */
const getGiftWithPurchaseData = ({ productData, selections = [], variant }) => {
    if (variant !== 'GIFTWITHPURCHASE') {
        return {
            isGwp: false,
            isGiftEditable: false,
            isGiftRedeemed: false,
        };
    }

    const thisGwpInCart = selections.find((item) => item.id === productData?.id && item.isGwpFree);
    const isGiftRedeemed = thisGwpInCart?.qty > 0;
    const anyGwpInCart = selections.some((item) => item.isGwpFree && item.qty > 0);
    const isGiftEditable = !anyGwpInCart || isGiftRedeemed;

    return {
        isGwp: true,
        isGiftEditable,
        isGiftRedeemed,
    };
};

export { getGiftWithPurchaseData, getPerksData };
